import React, { ChangeEvent, forwardRef, useRef, useEffect } from "react"
import cn from "classnames"

import { TextareaProps } from "./types"
import style from "./styles.module.scss"

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            className,
            fieldClassName,
            error,
            label,
            labelIcon,
            name,
            success,
            value,
            onChange,
            refLabel,
            testId = "textarea",
            ...props
        },
        ref
    ) => {
        const textareaRef = useRef<HTMLTextAreaElement | null>(null)

        useEffect(() => {
            if (textareaRef.current) {
                textareaRef.current.style.height = "auto"
                textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
            }
        }, [value])

        const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(event.target.value)
        }

        return (
            <div className={cn(style.textarea, className)}>
                {label && (
                    <label
                        data-testid={`${testId}__textarea-label`}
                        className={style.label}
                        htmlFor={`textarea-${name}`}
                        ref={refLabel}
                    >
                        {labelIcon}
                        <span className={style.input__labelText}>{label}</span>
                    </label>
                )}
                <textarea
                    data-testid={`${testId}__textarea-field`}
                    id={`textarea-${name}`}
                    className={cn(style.field, fieldClassName, { [style.field_error]: error })}
                    name={name}
                    ref={(el) => {
                        textareaRef.current = el
                        if (typeof ref === "function") ref(el)
                        else if (ref) ref.current = el
                    }}
                    value={value}
                    onChange={handleChange}
                    rows={1}
                    {...props}
                />
                {error && (
                    <div data-testid={`${testId}__textarea-error`} className={style.error}>
                        {error}
                    </div>
                )}
                {success && (
                    <div data-testid={`${testId}__textarea-success`} className={style.success}>
                        {success}
                    </div>
                )}
            </div>
        )
    }
)

Textarea.displayName = "Textarea"
